// ---------------------------------------------------------------------------
// Sync transport: the typed HTTP surface to the backend. The engine talks to the
// SyncTransport interface, so tests can drive it with an in-memory fake and the
// real app uses HttpSyncTransport (bearer token from MSAL).
// ---------------------------------------------------------------------------

export interface ChangePayload {
  entityType: string;
  entityId: string;
  op: 'upsert' | 'delete';
  payload: string | null;
  baseRevision: number;
  updatedAt: string;
}

export interface PushRequest {
  deviceId: string;
  changes: ChangePayload[];
}

export interface AppliedChange {
  entityType: string;
  entityId: string;
  revision: number;
  status: 'applied' | 'ignored' | 'conflict';
}

export interface Conflict {
  id: string;
  entityType: string;
  entityId: string;
  baseRevision: number;
  serverRevision: number;
  serverPayload: string | null;
  clientPayload: string | null;
  createdAt: string;
}

export interface PushResponse {
  serverRevision: number;
  results: AppliedChange[];
  conflicts: Conflict[];
}

export interface BlobRef {
  blobId: string;
  entityType: string;
  entityId: string;
  contentType: string;
  size: number;
  deleted: boolean;
  revision: number;
}

export interface DocumentChange {
  entityType: string;
  entityId: string;
  revision: number;
  payload: string | null;
  deleted: boolean;
  updatedAt: string;
  kind: 'doc' | 'blob';
  blob?: BlobRef | null;
}

export interface PullResponse {
  cursor: number;
  serverRevision: number;
  hasMore: boolean;
  changes: DocumentChange[];
}

export interface BootstrapResponse {
  userId: string;
  externalId: string;
  email: string | null;
  serverRevision: number;
  hasCloudData: boolean;
}

export interface MeResponse {
  userId: string;
  externalId: string;
  email: string | null;
  displayName: string | null;
  serverRevision: number;
}

export interface ResolveConflictRequest {
  conflictId: string;
  resolution: 'mine' | 'cloud' | 'merge';
  mergedPayload: string | null;
}

export interface SyncTransport {
  me(): Promise<MeResponse>;
  bootstrap(deviceId: string, platform: string): Promise<BootstrapResponse>;
  push(req: PushRequest): Promise<PushResponse>;
  pull(cursor: number): Promise<PullResponse>;
  conflicts(): Promise<Conflict[]>;
  resolveConflict(req: ResolveConflictRequest): Promise<PushResponse>;
  uploadBlob(entityType: string, entityId: string, bytes: Uint8Array, mime: string): Promise<{ blobId: string; revision: number }>;
  downloadBlob(blobId: string): Promise<{ bytes: Uint8Array; mime: string } | null>;
}

/** The user is not (or no longer) authenticated — surfaces as a re-login prompt. */
export class AuthError extends Error {}
/** The network is unreachable — the engine treats this as "offline", not a failure. */
export class OfflineError extends Error {}

export type TokenProvider = () => Promise<string | null>;

export class HttpSyncTransport implements SyncTransport {
  constructor(
    private readonly baseUrl: string,
    private readonly getToken: TokenProvider,
  ) {}

  private url(path: string): string {
    return `${this.baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    if (!token) throw new AuthError('not signed in');
    let res: Response;
    try {
      res = await fetch(this.url(path), {
        ...init,
        headers: {
          ...(init.headers ?? {}),
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (e) {
      throw new OfflineError((e as Error).message);
    }
    if (res.status === 401 || res.status === 403) throw new AuthError(`auth failed (${res.status})`);
    if (!res.ok) throw new Error(`${init.method ?? 'GET'} ${path} failed: ${res.status}`);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  me() {
    return this.request<MeResponse>('/api/me');
  }

  bootstrap(deviceId: string, platform: string) {
    return this.request<BootstrapResponse>('/api/sync/bootstrap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId, platform }),
    });
  }

  push(req: PushRequest) {
    return this.request<PushResponse>('/api/sync/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  }

  pull(cursor: number) {
    return this.request<PullResponse>(`/api/sync/pull?cursor=${cursor}`);
  }

  conflicts() {
    return this.request<Conflict[]>('/api/sync/conflicts');
  }

  resolveConflict(req: ResolveConflictRequest) {
    return this.request<PushResponse>('/api/sync/resolve-conflict', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
  }

  async uploadBlob(entityType: string, entityId: string, bytes: Uint8Array, mime: string) {
    const token = await this.getToken();
    if (!token) throw new AuthError('not signed in');
    const q = `entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`;
    let res: Response;
    try {
      res = await fetch(this.url(`/api/media/upload?${q}`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': mime },
        // Cast: a Uint8Array is a valid fetch BodyInit at runtime; the DOM lib's
        // generic ArrayBufferLike typing rejects it statically.
        body: bytes as unknown as BodyInit,
      });
    } catch (e) {
      throw new OfflineError((e as Error).message);
    }
    if (res.status === 401 || res.status === 403) throw new AuthError('auth failed');
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
    return (await res.json()) as { blobId: string; revision: number };
  }

  async downloadBlob(blobId: string) {
    const token = await this.getToken();
    if (!token) throw new AuthError('not signed in');
    let res: Response;
    try {
      res = await fetch(this.url(`/api/media/${blobId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      throw new OfflineError((e as Error).message);
    }
    if (res.status === 404) return null;
    if (res.status === 401 || res.status === 403) throw new AuthError('auth failed');
    if (!res.ok) throw new Error(`download failed: ${res.status}`);
    const buf = new Uint8Array(await res.arrayBuffer());
    return { bytes: buf, mime: res.headers.get('Content-Type') ?? 'application/octet-stream' };
  }
}
